import { Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { Button } from '@/components/ui/button';
import {
  formatBytes,
  uploadExperienceVideo,
  ACCEPTED_VIDEO_TYPES,
  MAX_VIDEO_BYTES,
  MediaRejectedError,
} from '@/data/storageUpload';
import { parseHeroVideoSource } from '@/util/util_heroVideo';

interface HeroVideoFieldProps {
  value: string;
  onChange: (url: string) => void;
}

/**
 * Hero video control: upload a file, or paste a YouTube link.
 *
 * Both land in the same `videoUrl` string — the experience page works out which
 * kind it is when it renders — so this is one field with two ways to fill it
 * rather than two fields that could disagree. Uploading is the better answer of
 * the two (no YouTube branding over the hero, no play button while it loads),
 * so it leads; the link stays for existing content and for anything too large
 * to host ourselves.
 */
export function HeroVideoField({ value, onChange }: HeroVideoFieldProps) {
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploading = progress !== null;
  const source = parseHeroVideoSource(value);

  async function handleFile(file: File) {
    setProgress(0);
    setError(null);
    try {
      onChange(await uploadExperienceVideo(file, setProgress));
    } catch (e) {
      // Only a file refused before it left the browser (too big, wrong format)
      // gets its own wording shown — it says what to fix. Anything else is a
      // Firebase StorageError, and "(storage/unauthorized)" tells an editor
      // nothing they can act on.
      setError(e instanceof MediaRejectedError ? e.message : 'Upload failed. Please try again.');
    } finally {
      setProgress(null);
    }
  }

  return (
    <div>
      {source?.kind === 'file' && (
        <div className='tw:relative tw:mb-3 tw:inline-block'>
          {/* Muted and loopless: this is a check that the right file uploaded,
              not a place to watch it. */}
          <video
            src={source.url}
            muted
            controls
            playsInline
            preload='metadata'
            className='tw:h-40 tw:w-auto tw:rounded-md tw:border'
          />
          <button
            type='button'
            onClick={() => onChange('')}
            className='tw:absolute tw:-top-2 tw:-right-2 tw:rounded-full tw:bg-destructive tw:p-1 tw:text-destructive-foreground'
          >
            <X className='tw:size-3.5' />
          </button>
        </div>
      )}

      <div className='tw:mb-3'>
        <Button type='button' variant='outline' onClick={() => inputRef.current?.click()} loading={uploading}>
          <Upload />
          {uploading ? `Uploading… ${Math.round((progress ?? 0) * 100)}%` : 'Upload Video'}
        </Button>

        {uploading && (
          <div className='tw:mt-2 tw:h-1.5 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted'>
            <div
              className='tw:h-full tw:rounded-full tw:bg-ed-gold tw:transition-[width] tw:duration-200'
              style={{ width: `${(progress ?? 0) * 100}%` }}
            />
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type='file'
        accept={ACCEPTED_VIDEO_TYPES.join(',')}
        className='tw:hidden'
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) handleFile(file);
        }}
      />

      <AutosaveTextInput
        currentValue={value}
        disabled={uploading}
        placeholder='…or paste a YouTube link'
        // The field plays exactly two things, so anything else is refused here
        // rather than saved and silently ignored on the page.
        validate={(v) =>
          !v.trim() || parseHeroVideoSource(v)
            ? null
            : 'Upload a video, or paste a YouTube link, e.g. https://www.youtube.com/watch?v=dQw4w9WgXcQ'
        }
        onCommitChange={(v) => onChange(v.trim())}
      />

      <p className='tw:mt-1.5 tw:text-xs tw:text-muted-foreground'>
        Plays muted and looping behind the title, and only when the layout format is “Video Hero”. Uploads up to{' '}
        {formatBytes(MAX_VIDEO_BYTES)} — an MP4 of 10–15 seconds, with no audio track, looks best and loads fastest.
      </p>

      {error && <p className='tw:mt-1.5 tw:text-sm tw:text-destructive'>{error}</p>}
    </div>
  );
}
